'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { v4 as uuidv4 } from 'uuid';
// Debounce function to prevent rapid updates
const debounce = (func, delay) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
};
const SideLabel = ({ labels }) => {
    const router = useRouter();
    const sectionRefs = useRef({});
    const [currentSection, setCurrentSection] = useState('');

    const sideLabel = labels || [];
    // Debounced function to update section smoothly
    const updateSection = useCallback(
        debounce((id) => setCurrentSection(id), 100),
        []
    );

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                let mostVisible = null;
                let highestRatio = 0;

                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
                        highestRatio = entry.intersectionRatio;
                        mostVisible = entry.target.id;
                    }
                });

                if (mostVisible) {
                    updateSection(mostVisible);
                }
            },
            {
                rootMargin: "-10% 0px -60% 0px", // Adjust for better visibility tracking
                threshold: [0.1, 0.25, 0.5, 0.75, 1],
            }
        );

        sideLabel.forEach((item) => {
            const sectionId = item.title?.replace(/ /g, "_");
            const sectionElement = document.getElementById(sectionId);
            if (sectionElement) {
                sectionRefs.current[sectionId] = sectionElement;
                observer.observe(sectionElement);
            }
        });

        return () => {
            Object.values(sectionRefs.current).forEach((section) => observer.unobserve(section));
        };
    }, [sideLabel, updateSection]);

    useEffect(() => {
        if (router.asPath && router.asPath.includes('#')) {
            const hash = router.asPath.split('#')[1];
            setCurrentSection(hash);
        }
    }, [router.asPath]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setCurrentSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-100px 0px -50% 0px', threshold: [0.1, 0.25, 0.5, 0.75, 1] }
        );

        sideLabel.forEach((item) => {
            const sectionId = item.title?.replace(/ /g, '_');
            const sectionElement = document.getElementById(sectionId);
            if (sectionElement) {
                sectionRefs.current[sectionId] = sectionElement;
                observer.observe(sectionElement);
            }
        });

        return () => {
            Object.values(sectionRefs.current).forEach((section) => observer.unobserve(section));
        };
    }, [sideLabel]);

    if (!sideLabel?.length) return
    return (
        <aside>
            <div className='flex flex-col space-y-4 p-5 bg-[#F9F9F9]'>
                {sideLabel?.map((item, index) => (
                    <SectionLink key={uuidv4()} item={{ ...item, index }} currentSection={currentSection} />
                ))}
            </div>
        </aside>
    );
}

export default SideLabel;

const SectionLink = ({ item, currentSection }) => {
    const sectionId = item.title?.replace(/ /g, '_');
    const isActive = currentSection === sectionId;

    return (
        <div className={`block text-xs cursor-pointer ${isActive ? 'border-l-4 border-solid border-green-500 text-blue-500 ' : ''}`}>
            <a
                className={`block text-left capitalize px-2 py-1 font-medium hover:text-blue-500  `}
                key={item.title + item.index}
                href={`#${sectionId}`}
                onClick={(e) => {
                    e.preventDefault();
                    const section = document.getElementById(sectionId);
                    if (section) {
                        const offset = 80; // Adjust margin from top
                        const top = section.getBoundingClientRect().top + window.scrollY - offset;
                        window.scrollTo({ top, behavior: 'smooth' });
                    }
                }}

            >
                {item.title}
            </a>
        </div>
    );
};